import { useCallback, useEffect, useRef } from "react";

const INTERACTION_SETTLE_DELAY_MS = 900;

const getCycleDistance = (rail, itemCount) => {
  const firstItem = rail.children[0];
  const firstRepeatedItem = rail.children[itemCount];
  if (!firstItem || !firstRepeatedItem) return 0;

  const firstRect = firstItem.getBoundingClientRect();
  const repeatedRect = firstRepeatedItem.getBoundingClientRect();
  const rectDistance = repeatedRect.left - firstRect.left;
  if (rectDistance > 0) return rectDistance;

  return Math.max(0, firstRepeatedItem.offsetLeft - firstItem.offsetLeft);
};

const normalizeCycleOffset = (value, cycleDistance) =>
  ((value % cycleDistance) + cycleDistance) % cycleDistance;

export default function useContinuousHorizontalScroll({
  railRef,
  enabled,
  itemCount,
  pixelsPerSecond = 24,
}) {
  const isPointerInteractingRef = useRef(false);
  const isTimedPauseActiveRef = useRef(false);
  const pauseTimeoutRef = useRef();
  const syncPauseStateRef = useRef(() => {});
  const cycleDistanceRef = useRef(0);

  const pauseFor = useCallback((durationMs = INTERACTION_SETTLE_DELAY_MS) => {
    if (typeof window === "undefined") return;

    isTimedPauseActiveRef.current = true;
    syncPauseStateRef.current();
    if (pauseTimeoutRef.current) {
      window.clearTimeout(pauseTimeoutRef.current);
    }

    if (typeof window.setTimeout !== "function") {
      isTimedPauseActiveRef.current = false;
      syncPauseStateRef.current();
      return;
    }

    pauseTimeoutRef.current = window.setTimeout(() => {
      isTimedPauseActiveRef.current = false;
      pauseTimeoutRef.current = undefined;
      syncPauseStateRef.current();
    }, Math.max(0, durationMs));
  }, []);

  const scrollByViewport = useCallback(
    (direction) => {
      const rail = railRef.current;
      const cycleDistance = cycleDistanceRef.current;
      if (!rail || cycleDistance <= 0 || !Number.isFinite(direction) || direction === 0) {
        return false;
      }

      pauseFor();
      const targetScrollLeft = normalizeCycleOffset(
        rail.scrollLeft + rail.clientWidth * direction,
        cycleDistance
      );

      if (typeof rail.scrollTo === "function") {
        rail.scrollTo({ left: targetScrollLeft, behavior: "auto" });
      } else {
        rail.scrollLeft = targetScrollLeft;
      }
      return true;
    },
    [pauseFor, railRef]
  );

  useEffect(() => {
    if (
      !enabled ||
      itemCount <= 1 ||
      pixelsPerSecond <= 0 ||
      typeof window === "undefined" ||
      typeof document === "undefined"
    ) {
      return undefined;
    }

    const rail = railRef.current;
    if (!rail) return undefined;

    const reducedMotionQuery =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : null;

    const syncMeasurements = () => {
      const cycleDistance = getCycleDistance(rail, itemCount);
      const canScroll = cycleDistance > 0 && rail.scrollWidth > rail.clientWidth;
      cycleDistanceRef.current = canScroll ? cycleDistance : 0;

      if (!canScroll) {
        rail.removeAttribute("data-auto-scroll-ready");
        rail.style.removeProperty("--event-loop-distance");
        rail.style.removeProperty("--event-loop-duration");
        return;
      }

      rail.style.setProperty("--event-loop-distance", `${-cycleDistance}px`);
      rail.style.setProperty(
        "--event-loop-duration",
        `${Math.max(1, cycleDistance / pixelsPerSecond)}s`
      );
      rail.setAttribute("data-auto-scroll-ready", "true");

      if (rail.scrollLeft < 0 || rail.scrollLeft >= cycleDistance) {
        rail.scrollLeft = normalizeCycleOffset(rail.scrollLeft, cycleDistance);
      }
    };

    const normalizeScrollPosition = () => {
      const cycleDistance = cycleDistanceRef.current;
      if (cycleDistance <= 0) return;

      const currentScrollLeft = rail.scrollLeft;
      if (currentScrollLeft >= 0 && currentScrollLeft < cycleDistance) return;

      rail.scrollLeft = normalizeCycleOffset(currentScrollLeft, cycleDistance);
    };

    const syncPauseState = () => {
      const isPaused =
        document.hidden ||
        reducedMotionQuery?.matches ||
        isPointerInteractingRef.current ||
        isTimedPauseActiveRef.current;

      if (isPaused) {
        rail.setAttribute("data-auto-scroll-paused", "true");
      } else {
        rail.removeAttribute("data-auto-scroll-paused");
      }
    };
    syncPauseStateRef.current = syncPauseState;

    const handlePointerDown = () => {
      isPointerInteractingRef.current = true;
      syncPauseState();
    };

    const handlePointerEnd = () => {
      if (!isPointerInteractingRef.current) return;
      isPointerInteractingRef.current = false;
      pauseFor();
    };

    const handleWheel = () => {
      pauseFor();
    };

    const resizeObserver =
      typeof window.ResizeObserver === "function"
        ? new window.ResizeObserver(syncMeasurements)
        : null;

    resizeObserver?.observe(rail);
    rail.addEventListener("pointerdown", handlePointerDown);
    rail.addEventListener("wheel", handleWheel, { passive: true });
    rail.addEventListener("scroll", normalizeScrollPosition, { passive: true });
    window.addEventListener("pointerup", handlePointerEnd);
    window.addEventListener("pointercancel", handlePointerEnd);
    window.addEventListener("resize", syncMeasurements);
    document.addEventListener("visibilitychange", syncPauseState);
    reducedMotionQuery?.addEventListener?.("change", syncPauseState);
    syncMeasurements();
    syncPauseState();

    return () => {
      if (pauseTimeoutRef.current) {
        window.clearTimeout(pauseTimeoutRef.current);
        pauseTimeoutRef.current = undefined;
      }
      isPointerInteractingRef.current = false;
      isTimedPauseActiveRef.current = false;
      cycleDistanceRef.current = 0;
      syncPauseStateRef.current = () => {};
      resizeObserver?.disconnect();
      rail.removeEventListener("pointerdown", handlePointerDown);
      rail.removeEventListener("wheel", handleWheel);
      rail.removeEventListener("scroll", normalizeScrollPosition);
      window.removeEventListener("pointerup", handlePointerEnd);
      window.removeEventListener("pointercancel", handlePointerEnd);
      window.removeEventListener("resize", syncMeasurements);
      document.removeEventListener("visibilitychange", syncPauseState);
      reducedMotionQuery?.removeEventListener?.("change", syncPauseState);
      rail.removeAttribute("data-auto-scroll-ready");
      rail.removeAttribute("data-auto-scroll-paused");
      rail.style.removeProperty("--event-loop-distance");
      rail.style.removeProperty("--event-loop-duration");
    };
  }, [enabled, itemCount, pauseFor, pixelsPerSecond, railRef]);

  return { pauseFor, scrollByViewport };
}
